import * as THREE from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { GlitchPass } from 'three/addons/postprocessing/GlitchPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'

import vertex from '../shaders/vertex.glsl'
import fragment from '../shaders/fragment.glsl'

const clamp = (number, min, max) => {
  return Math.max(min, Math.min(number, max))
}

export default class WebglBackground {
  constructor(dom, img) {
    this.container = dom
    this.img = img.currentSrc
    this.width = this.container.offsetWidth
    this.height = this.container.offsetHeight

    this.scene = new THREE.Scene()

    this.renderer = new THREE.WebGLRenderer()
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(this.width, this.height)
    this.renderer.setClearColor(0xeeeeee, 1)

    // this.renderer.physicallyCorrectLights = true
    // this.renderer.outputEncoding = THREE.sRGBEncoding

    this.container.appendChild(this.renderer.domElement)

    // this.camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 100)

    const frustumSize = 1
    this.camera = new THREE.OrthographicCamera(
      frustumSize / -2,
      frustumSize / 2,
      frustumSize / 2,
      frustumSize / -2,
      -1000,
      1000
    )
    this.camera.position.set(0, 0, 2)

    this.time = 0
    this.mouse = {
      x: 0,
      y: 0,
      prevX: 0,
      prevY: 0,
      vX: 0,
      vY: 0,
    }

    this.isPlaying = true

    this.settings()
    this.addObjects()
    this.resize()
    this.render()
    this.setupResize()
    this.mouseEvents()
  }

  mouseEvents() {
    window.addEventListener('mousemove', (e) => {
      this.mouse.x = e.clientX / this.width
      this.mouse.y = e.clientY / this.height

      this.mouse.vX = this.mouse.x - this.mouse.prevX
      this.mouse.vY = this.mouse.y - this.mouse.prevY

      this.mouse.prevX = this.mouse.x
      this.mouse.prevY = this.mouse.y
    })
  }

  settings() {
    this.settings = {
      grid: 35,
      mouse: 0.5,
      strength: 0.6,
      relaxation: 0.9,
    }
  }

  setupResize() {
    window.addEventListener('resize', this.resize.bind(this))
  }

  resize() {
    this.width = this.container.offsetWidth
    this.height = this.container.offsetHeight
    this.renderer.setSize(this.width, this.height)
    this.camera.aspect = this.width / this.height

    // image cover
    this.imageAspect = 1
    let a1
    let a2
    if (this.height / this.width > this.imageAspect) {
      a1 = (this.width / this.height) * this.imageAspect
      a2 = 1
    } else {
      a1 = 1
      a2 = this.height / this.width / this.imageAspect
    }

    this.material.uniforms.resolution.value.x = this.width
    this.material.uniforms.resolution.value.y = this.height
    this.material.uniforms.resolution.value.z = a1
    this.material.uniforms.resolution.value.w = a2

    this.camera.updateProjectionMatrix()
    this.regenerateGrid()
  }

  regenerateGrid() {
    this.size = this.settings.grid

    const width = this.size
    const height = this.size

    const size = width * height
    const data = new Float32Array(4 * size)

    for (let i = 0; i < size; i++) {
      const r = Math.random() * 255 - 125
      const r1 = Math.random() * 255 - 125

      const stride = i * 4

      data[stride] = r
      data[stride + 1] = r1
      data[stride + 2] = r
    }

    // used the buffer to create a DataTexture
    this.texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.FloatType)
    this.texture.magFilter = this.texture.minFilter = THREE.NearestFilter
    if (this.material) {
      this.material.uniforms.uDataTexture.value = this.texture
      this.material.uniforms.uDataTexture.value.needsUpdate = true
    }
  }

  addObjects() {
    this.regenerateGrid()

    this.geometry = new THREE.PlaneGeometry(1, 1, 1, 1)
    const texture = new THREE.TextureLoader().load(this.img)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.needsUpdate = true
    this.material = new THREE.ShaderMaterial({
      extensions: {
        derivatives: '#extension GL_OES_standard_derivatives : enable',
      },
      side: THREE.DoubleSide,
      uniforms: {
        time: {
          value: 0,
        },
        resolution: {
          value: new THREE.Vector4(),
        },
        uTexture: {
          value: texture,
        },
        uDataTexture: {
          value: this.texture,
        },
      },
      vertexShader: vertex,
      fragmentShader: fragment,
    })
    this.mesh = new THREE.Mesh(this.geometry, this.material)
    this.scene.add(this.mesh)

    this.composer = new EffectComposer(this.renderer)
    this.composer.addPass(new RenderPass(this.scene, this.camera))

    this.glitchPass = new GlitchPass(100)
    this.glitchPass.goWild = true
    this.glitchPass.renderToScreen = true
    this.glitchPass.curF = 0.0
    this.composer.addPass(this.glitchPass)
    this.glitchPass.enabled = true

    setTimeout(() => {
      this.glitchPass.enabled = false
    }, 1000)

    setInterval(() => {
      this.glitchPass.enabled = true
      setTimeout(() => {
        this.glitchPass.enabled = false
      }, 2000)
    }, 10000)

    this.outputPass = new OutputPass()
    this.composer.addPass(this.outputPass)
  }

  updateDataTexture() {
    let data = this.texture.image.data
    for (let i = 0; i < data.length; i += 4) {
      data[i] *= this.settings.relaxation
      data[i + 1] *= this.settings.relaxation
    }
    let gridMouseX = this.size * this.mouse.x
    let gridMouseY = this.size * (1 - this.mouse.y)
    let maxDist = this.size * this.settings.mouse
    let aspect = this.height / this.width

    for (let i = 0; i < this.size; i++) {
      for (let j = 0; j < this.size; j++) {
        let distance = (gridMouseX - i) ** 2 / aspect + (gridMouseY - j) ** 2
        let maxDistSq = maxDist ** 2

        if (distance < maxDistSq) {
          let index = 4 * (i + this.size * j)

          let power = maxDist / Math.sqrt(distance)
          power = clamp(power, 0, 10)

          data[index] += this.settings.strength * 100 * this.mouse.vX * power
          data[index + 1] -= this.settings.strength * 100 * this.mouse.vY * power
        }
      }
    }
    this.mouse.vX *= 0.9
    this.mouse.vY *= 0.9
    this.texture.needsUpdate = true
  }

  render() {
    if (!this.isPlaying) return
    this.time += 0.05
    this.updateDataTexture()
    this.material.uniforms.time.value = this.time
    requestAnimationFrame(this.render.bind(this))
    this.composer.render(this.scene, this.camera)
  }
}
